import { MemberListItemDto } from '../../types';
import { ExportColumn, ExportFilterValue, ExportRow, ExportSheet } from '../../lib/export';

export const MEMBER_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'status', header: 'Status', width: 12 },
  { key: 'firstName', header: 'First Name' },
  { key: 'lastName', header: 'Last Name' },
  { key: 'email', header: 'Email', width: 34 },
  { key: 'role', header: 'Role' },
  { key: 'brgMembership', header: 'BRG Membership', width: 48 },
  { key: 'brgCount', header: 'BRG Count', width: 12 },
];

export interface MemberExportOptions {
  /** Admins export every member; leads are limited to their assigned BRGs. */
  isAdmin: boolean;
  /** BRG ids the current user may see. Ignored when `isAdmin` is true. */
  allowedBrgIds: number[];
  /** Role label as shown in the grid, which narrows to the selected BRGs. */
  roleDisplay: (member: MemberListItemDto) => string;
}

/**
 * Maps the members currently shown on the page into export rows.
 *
 * Row set already reflects the page's search and BRG filters; this adds the
 * role-based scope so a lead never exports members or BRG names outside the
 * BRGs they are assigned to.
 */
export function buildMemberExportRows(
  members: MemberListItemDto[],
  options: MemberExportOptions
): ExportRow[] {
  const allowed = options.isAdmin ? null : new Set(options.allowedBrgIds);

  const rows: ExportRow[] = [];
  for (const member of members) {
    const memberships = allowed
      ? member.brgMemberships.filter((m) => allowed.has(Number(m.brgId)))
      : member.brgMemberships;

    // A lead exporting a member with no in-scope BRG would be leaking a record
    // they cannot otherwise act on.
    if (allowed && memberships.length === 0) continue;

    const brgNames = memberships
      .map((m) => m.brgName)
      .filter((name): name is string => Boolean(name))
      .sort((a, b) => a.localeCompare(b));

    rows.push({
      status: member.isActive ? 'Active' : 'Inactive',
      firstName: member.firstName || '',
      lastName: member.lastName || '',
      email: member.email || '',
      role: options.roleDisplay(member),
      brgMembership: brgNames.join('; '),
      brgCount: brgNames.length,
    });
  }
  return rows;
}

export interface MemberFilterState {
  search: string;
  selectedBrgIds: number[];
  brgNameById: Map<number, string>;
}

export function buildMemberExportFilters(state: MemberFilterState): ExportFilterValue[] {
  const brgValue =
    state.selectedBrgIds.length === 0
      ? 'All BRGs'
      : state.selectedBrgIds
          .map((id) => state.brgNameById.get(id) ?? `BRG ${id}`)
          .sort((a, b) => a.localeCompare(b))
          .join(', ');

  return [
    { label: 'Search', value: state.search.trim() || 'None' },
    { label: 'BRG', value: brgValue },
  ];
}

export function buildMemberExportSheet(rows: ExportRow[]): ExportSheet {
  return {
    name: 'Members',
    columns: MEMBER_EXPORT_COLUMNS,
    rows,
    summaryTitle: 'Summary',
    summary: [{ label: 'Members exported', value: rows.length }],
  };
}
